/**
 * The message banner's behavior half: the tab's chat's arrivals (`WebChatHub.onArrived`) through
 * the banner queue, while the messages panel is closed. Opening the panel drops what shows and
 * waits; a message that arrived while it was open never raises a banner later. Only a page showing the header with the
 * client's messages in it (the account piece, or the studio header) raises banners. Signed out, nothing
 * shows or waits, so a private preview never outlives its session (Codex review 2026-10-26). Draws nothing.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { useWebSession } from '../../api/session';
import { useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { BANNER_MS, advanceBanners, bannerOf, clearBanners, emptyBanners, offerBanner, type BannerQueue } from '../../messages/bannerQueue';
import { useMessagesPanel } from '../../messages/panelContext';
import { useWebChat } from '../../messages/WebChat';
import { contract, type MessageBannerActions, type MessageBannerEvents, type MessageBannerState } from './contract';

export type MessageBannerRuntime = PieceRuntime<typeof contract, MessageBannerState, MessageBannerActions>;

/** The way out, before the next banner slides in. */
export const BANNER_OUT_MS = 280;

export function useMessageBanner(input: PieceInput<MessageBannerEvents> = {}): MessageBannerRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const { hub, state: chat } = useWebChat();
  const session = useWebSession();
  const userId = session.status === 'signedIn' ? (session.user?.id ?? null) : null;
  const panel = useMessagesPanel();
  const studio = useSiteStudio().data;
  const [queue, setQueue] = useState<BannerQueue>(emptyBanners);
  const [leaving, setLeaving] = useState(false);
  const openRef = useRef(panel.open);
  openRef.current = panel.open;
  // Only a page showing the header with the messages in it raises banners: never a page without that header, such as a
  // custom site's landing page (card 4 walk, Gabriel 2026-11-01).
  const headerRef = useRef(panel.headerShown);
  headerRef.current = panel.headerShown;

  useEffect(
    () =>
      hub.onArrived((items) => {
        // With the panel open, or no header, a message shows in the stream only, and never raises a banner later.
        setQueue((current) =>
          items.reduce((next, item) => {
            const banner = bannerOf(item);
            if (!banner) return next;
            const offered = offerBanner(next, banner);
            return openRef.current || !headerRef.current ? clearBanners(offered) : offered;
          }, current),
        );
      }),
    [hub],
  );

  // The session ended (sign-out, another person): nothing of it shows, waits or counts as offered.
  useEffect(() => {
    setLeaving(false);
    setQueue(emptyBanners);
  }, [userId]);

  // The panel opened, or the header left the page: nothing shows or waits.
  useEffect(() => {
    if (!panel.open && panel.headerShown) return;
    setLeaving(false);
    setQueue(clearBanners);
  }, [panel.open, panel.headerShown]);

  // Six seconds, then the way out, then the next one.
  const showingId = queue.showing?.id ?? null;
  useEffect(() => {
    if (!showingId) return undefined;
    setLeaving(false);
    let out: ReturnType<typeof setTimeout> | undefined;
    const stay = setTimeout(() => {
      setLeaving(true);
      out = setTimeout(() => setQueue(advanceBanners), BANNER_OUT_MS);
    }, BANNER_MS);
    return () => {
      clearTimeout(stay);
      clearTimeout(out);
    };
  }, [showingId]);

  const { openMessages } = panel;
  const actions = useMemo<MessageBannerActions>(
    () => ({
      open: () => {
        if (!showingId) return;
        emit('opened', { id: showingId });
        openMessages({ atEntryId: showingId });
      },
    }),
    [emit, openMessages, showingId],
  );

  return {
    state: {
      banner: chat.live ? queue.showing : null,
      leaving,
      queued: chat.live && queue.waiting.length > 0,
      studioName: studio?.name ?? '',
      logoUrl: studio?.logoUrl ?? null,
    },
    actions,
    options,
  };
}
